// services/properties/hooks.ts

'use client'

import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod/mini'

import { getAmplifyClient } from '@/services/amplifyClient'
import { toSupportedKeys } from '@/services/property/property.adapter'
import { getProperty } from '@/services/property/property.server'
import type { SupportedKeys } from '@/services/property/property.type'
import { Language } from '@/types/translations'
import { propertyQueryKey } from './property.keys'
import { useUserContext } from '@/context/UserContext'
import { buildOptionFilter, OptionFieldFilter } from '../queries/utils/filters'
import { staticQueryPreset } from '@/services/query-presets'
import type { RawOptionListDefinition } from './option.access'
import { pickLabel } from './option.normalized'

const client = getAmplifyClient()
const uuidSchema = z.uuid()

// -----------------------------------------------------------------------------
// Types & constantes
// -----------------------------------------------------------------------------

type GetPropertyParams = {
  enabled?: boolean
  lang?: Language
}

/**
 * Property par défaut (ex « skop ») utilisée lorsque l'id n'est pas fourni
 * ou que l'on passe explicitement 'skop' côté front.
 */
const defaultPropertyId = process.env.NEXT_PUBLIC_DEFAULT_PROPERTY!

// -----------------------------------------------------------------------------
// Helpers de type & de garde
// -----------------------------------------------------------------------------

/**
 * Structure normalisée pour la définition de propriété.
 * - `supportedKeys` : liste de toutes les clés supportées
 * - `byKey` : index par key (ou id de secours)
 * - `lists` : [key, listId] pour toutes les LIST
 * - `refKey` : clé de référence (isRef)
 */
export type PropertyDefinitionNormalized = {
  lists: [string, string][]
  listDefinitions: RawOptionListDefinition[]
  byKey: Record<string, SupportedKeys>
  supportedKeys: SupportedKeys[]
  refKey: string
  definitionVersion: string
  isCustomProperty: boolean
  organizationId: string
  organizationName: string
  optionFilter?: OptionFieldFilter
}

// -----------------------------------------------------------------------------
// Hook: useGetProperty
// - Récupère la définition de propriété (sans options)
// - Normalise la réponse pour faciliter l'accès aux données & updates
// -----------------------------------------------------------------------------

export const useGetProperty = (
  params?: GetPropertyParams,
): UseQueryResult<PropertyDefinitionNormalized> => {
  const { enabled, lang } = params ?? {}
  const queryClient = useQueryClient()
  const { customSettings, authorization, organizationScope, ownership } =
    useUserContext()
  const id = customSettings?.propertyId

  const activeOrganizationId = organizationScope.activeOrganizationId
  const userID = authorization?.user?.id
  const resolvedUserId = userID ?? ownership?.byUserID
  const isAdmin = authorization?.user?.role === 'ADMIN'

  const propertyId = id && id !== 'skop' ? id : defaultPropertyId

  return useQuery({
    queryKey: propertyQueryKey(propertyId),
    queryFn: () => getProperty({ id: propertyId, client }),
    enabled: !!propertyId && enabled !== false,
    gcTime: staticQueryPreset.gcTime,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    meta: {
      persistence: 'none',
    },
    /**
     * On réutilise immédiatement une éventuelle version déjà en cache
     * pour limiter les rerenders et éviter les états "vides" transitoires.
     */
    initialData: () => queryClient.getQueryData(propertyQueryKey(propertyId)),
    /**
     * On dérive directement la structure normalisée dans `select` :
     * - moins de compute dans les composants
     * - références stables pour les sous-structures
     */
    select: (property) => {
      if (!property) {
        return {
          lists: [],
          listDefinitions: [],
          byKey: {},
          supportedKeys: [],
          refKey: '',
          definitionVersion: '',
          isCustomProperty: false,
          organizationId: '',
          organizationName: '',
          optionFilter: undefined,
        }
      }

      const { version, fields, organizationPropertiesId, organization } =
        property

      const definitionVersion = version
      const isCustomProperty = propertyId !== defaultPropertyId

      // 1. Convertir en SupportedKeys (ordre, flags, etc.)
      const supportedKeys = toSupportedKeys(
        fields.items.map((f) => ({ id: f.id, field: f.field })),
        lang,
      )

      // 2. Extraire toutes les LIST (id de la liste "source")
      const rawListDefinitions: RawOptionListDefinition[] = fields.items
        .filter((f) => f.field?.type === 'LIST')
        .map((f) => {
          const field = f.field
          const alias = uuidSchema.safeParse(field.value)
          const rawId = alias.success ? alias.data : field.id

          return {
            propertyKey: field.key,
            key: field.key,
            label: pickLabel(field.label, field.translations?.items, lang),
            propertyFieldId: field.id,
            listId: rawId,
            propertyOrganizationID: field.organizationFieldsId,
          }
        })

      const rawListEntries: [string, string][] = rawListDefinitions.map(
        ({ key, listId }) => [key, listId],
      )

      // 3. Dédupliquer (clé + id)
      const listEntries = Array.from(
        new Map(
          rawListEntries.map(([k, v]) => [
            `${k}:${v}`,
            [k, v] as [string, string],
          ]),
        ).values(),
      )

      // 4. Garder uniquement les ids valides (UUID)
      const lists: [string, string][] = listEntries.filter(
        ([, listId]) => uuidSchema.safeParse(listId).success,
      )
      const listDefinitions = rawListDefinitions.filter(
        ({ listId }) => uuidSchema.safeParse(listId).success,
      )

      // 5. Indexer les SupportedKeys par key (ou id)
      const byKey: Record<string, SupportedKeys> = {}
      for (const sk of supportedKeys) {
        const k = sk.key ?? sk.id
        if (!k) continue
        byKey[k] = sk
      }

      // 6. Clé de référence
      const refKey = supportedKeys.find((key) => key.isRef)?.key || ''

      // 7. Construire le filter pour les options
      const optionFilter = buildOptionFilter({
        activeOrganizationId,
        propertyOrganizationID: organizationPropertiesId,
        userID: resolvedUserId,
        isAdmin,
        scopeKind: organizationScope.scopeKind,
      })

      return {
        lists,
        listDefinitions,
        byKey,
        supportedKeys,
        refKey,
        definitionVersion,
        isCustomProperty,
        organizationId: organizationPropertiesId,
        organizationName: organization.name,
        optionFilter,
      }
    },
  })
}
